import { ChannelIcon } from '../../components/ui/ChannelIcon';
import { cn } from '../../lib/utils';
import { DESTINATIONS, type PostDestinationId } from '../../lib/publishing/destinations';
import type { PostPlatform } from '../../types/publishing';

// Publishing composer §A — the tickable DESTINATION row above the caption.
//
// Five destinations over three platforms, each with its channel logo so the
// target is obvious at a glance. Facebook and Instagram appear TWICE (feed and
// story), so a placement indicator is what tells them apart — the logo alone
// would be ambiguous.
//
// Destinations whose platform isn't connected for the selected account render
// disabled with a reason, rather than vanishing: an AM looking for "Instagram"
// on an account that has no IG connection needs to see that it exists and is
// unavailable, not wonder whether the feature is missing.

export function DestinationPicker({
  selected,
  connected,
  disabled,
  onToggle,
}: {
  selected: PostDestinationId[];
  connected: Record<PostPlatform, boolean>;
  disabled?: boolean;
  onToggle: (id: PostDestinationId, next: boolean) => void;
}) {
  const chosen = new Set(selected);

  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between">
        <label className="block text-sm font-medium text-slate-700">Post to</label>
        {selected.length === 0 && (
          <span className="text-xs text-slate-400">Pick at least one destination</span>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {DESTINATIONS.map((d) => {
          const available = connected[d.platform];
          const active = chosen.has(d.id);
          return (
            <button
              key={d.id}
              type="button"
              role="checkbox"
              aria-checked={active}
              aria-label={d.label}
              disabled={!available || disabled}
              title={available ? d.label : `${d.label} — not connected for this account`}
              onClick={() => onToggle(d.id, !active)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition',
                active
                  ? 'border-kyck-500 bg-kyck-50 font-medium text-kyck-900'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50',
                (!available || disabled) && 'cursor-not-allowed opacity-40 hover:bg-white',
              )}
            >
              <ChannelIcon channel={d.platform} className="h-4 w-4" />
              <span>{d.shortLabel}</span>
              {d.placement === 'story' && (
                <span
                  className={cn(
                    'rounded px-1 text-[9px] font-semibold uppercase tracking-wide',
                    active ? 'bg-kyck-200 text-kyck-900' : 'bg-slate-100 text-slate-500',
                  )}
                >
                  Story
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
